import React, { useState, useEffect, useRef } from 'react';
import './SonoTerapiaScreen.css';

// Categorías y metadatos
const CATEGORIES = [
  {
    key: 'hiperactividad',
    label: 'Relajación (Hiperactividad)',
    desc: 'Reduce la hiperactividad y promueve calma estable.',
    palette: 'calido',
    icon: '🧠'
  },
  {
    key: 'tda',
    label: 'Atención y Foco (TDA)',
    desc: 'Estimula la concentración sostenida y el foco mental.',
    palette: 'estimulo',
    icon: '🎯'
  },
  {
    key: 'conectividad',
    label: 'Conectividad (Frecuencias Gamma)',
    desc: 'Favorece estados de alta integración neuronal.',
    palette: 'gamma',
    icon: '⚡'
  }
];

// Generar lista de pistas (simples: 1.mp3..4.mp3). Se reproducen en loop hasta 5 minutos.
const buildTracks = (cat) => [1,2,3,4].map(n => ({
  file: `/sonoterapia/${cat}/${n}.mp3`,
  label: `${n}`
}));

export default function SonoTerapiaScreen({ onBack }) {
  const [category, setCategory] = useState(CATEGORIES[0].key);
  const [tracks, setTracks] = useState(buildTracks(CATEGORIES[0].key));
  const [currentTrackIdx, setCurrentTrackIdx] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const audioRef = useRef(null);
  const loopTimerRef = useRef(null); // controla el avance cada 5 min a la siguiente pista
  const wakeLockRef = useRef(null); // Wake Lock para móviles
  const [treatmentDone, setTreatmentDone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Actualizar tracks cuando cambia categoría
  useEffect(() => {
    setTracks(buildTracks(category));
    setCurrentTrackIdx(0);
    setStartTime(null);
    // Al cambiar de categoría, detener secuencia previa
    clearTimeout(loopTimerRef.current);
    const a = audioRef.current;
    if (a) { try { a.pause(); a.currentTime = 0; } catch(_){} }
  }, [category]);

  // Detectar mobile para ajustar estilos ligeros (evitar backgroundAttachment fixed)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 780px)');
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Iniciar reproducción y forzar loop hasta 5 minutos (300000 ms)
  const verifySource = async (url) => {
    try {
      // Intentar HEAD primero
      const head = await fetch(url, { method: 'HEAD', cache: 'no-store' });
      if (head.ok) {
        const ct = head.headers.get('content-type') || '';
        return { ok: true, contentType: ct, status: head.status };
      }
      // Algunos servidores no aceptan HEAD; probar GET con rango mínimo
      const get = await fetch(url, { headers: { Range: 'bytes=0-1' }, cache: 'no-store' });
      const ct2 = get.headers.get('content-type') || '';
      return { ok: get.ok, contentType: ct2, status: get.status };
    } catch (e) {
      return { ok: false, contentType: '', status: 0 };
    }
  };

  // Reproduce una pista concreta y programa el salto a la siguiente a los 5 minutos
  const playTrack = async (idx) => {
    const audio = audioRef.current;
    if (!audio) return;
    const url = encodeURI(tracks[idx].file);
    setErrorMsg('');
    setTreatmentDone(false);
    // Verificar fuente
    const check = await verifySource(url);
    if (!check.ok || (check.contentType && !/audio\/(mpeg|mp3|mpeg3)/i.test(check.contentType))) {
      // Si falla esta pista, intenta con la siguiente para mantener continuidad
      const next = (idx + 1) % tracks.length;
      setErrorMsg(`Pista no disponible o incompatible: ${url}. Avanzando a la siguiente.`);
      setCurrentTrackIdx(next);
      return playTrack(next);
    }
    // Cargar y reproducir
    try {
      audio.pause();
      audio.src = url;
      audio.loop = false; // no hacer loop infinito: avanzaremos manualmente en la playlist
      audio.load();
    } catch(_){}
    const onCanPlay = () => {
      audio.currentTime = 0;
      audio.play().then(() => {
  setCurrentTrackIdx(idx);
        setStartTime(Date.now());
        // Intentar mantener la pantalla encendida en móviles
        if ('wakeLock' in navigator && !wakeLockRef.current) {
          navigator.wakeLock.request('screen').then(s => { wakeLockRef.current = s; }).catch(()=>{});
        }
        clearTimeout(loopTimerRef.current);
        // A los 5 minutos, pasar a la siguiente pista o finalizar si es la última
        loopTimerRef.current = setTimeout(() => {
          const next = idx + 1;
          if (next < tracks.length) {
            playTrack(next);
          } else {
            // Fin de la lista: marcar terminado y detener audio
            try { audio.pause(); audio.currentTime = 0; } catch(_){}
            setStartTime(null);
            setTreatmentDone(true);
          }
        }, 300000);
      }).catch(() => {
        setErrorMsg('No se pudo iniciar la reproducción. Permite audio en el navegador.');
      });
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
    };
    const onError = () => {
      const next = (idx + 1) % tracks.length;
      setErrorMsg(`Error reproduciendo ${url}. Probando siguiente pista.`);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
      playTrack(next);
    };
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onError);
  };

  // Botón principal: inicia secuencia desde la pista seleccionada y avanza 1→4→1...
  const handlePlay = async () => {
    clearTimeout(loopTimerRef.current);
    await playTrack(currentTrackIdx);
  };

  // Cambio de pista
  const handleChangeTrack = (idx) => {
    setCurrentTrackIdx(idx);
    setStartTime(null);
    clearTimeout(loopTimerRef.current);
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  };

  // Detener reproducción manualmente
  const handleStop = () => {
    clearTimeout(loopTimerRef.current);
    const audio = audioRef.current;
    if (audio) {
      try { audio.pause(); audio.currentTime = 0; } catch(_){}
    }
    setStartTime(null);
  };

  // Limpiar timers al desmontar
  useEffect(() => {
    return () => {
      clearTimeout(loopTimerRef.current);
      const a = audioRef.current; if (a) { try { a.pause(); } catch(_){} }
      try { wakeLockRef.current && wakeLockRef.current.release(); } catch(_){}
    };
  }, []);

  // Progreso visual del tiempo (0 - 5min)
  const elapsedMs = startTime ? Date.now() - startTime : 0;
  const progressPct = Math.min(100, (elapsedMs / 300000) * 100);

  const catMeta = CATEGORIES.find(c => c.key === category);

  return (
    <div
      className={`sonoterapia-container palette-${catMeta.palette}`}
      style={{ backgroundImage: "url('/fondo.png')", backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: isMobile ? 'scroll' : 'fixed' }}
    >      
      <div className="sonoterapia-overlay" />
      <div className="sonoterapia-content">
        <button className="sonoterapia-back" onClick={onBack}>← Volver</button>
        <div className="sonoterapia-header">
          <div>
            <h1 className="sonoterapia-title">SonoTerapia</h1>
            <p className="sonoterapia-sub">Audio-Modulación Terapéutica ◦ Lista de reproducción</p>
          </div>
          <img src="/sonoterapia/braineka-logo.png" alt="Braineka" className="sonoterapia-logo" onError={(e)=>{ e.currentTarget.onerror = null; e.currentTarget.style.display = 'none'; }} />
        </div>
        <div className="sono-warning" role="note">
          <img
            src="/sonoterapia/auriculares.png"
            alt="Auriculares"
            onError={(e)=>{ e.currentTarget.onerror = null; e.currentTarget.src = '/auriculares.png'; }}
          />
          <span>Usar con auriculares o audífonos</span>
        </div>
        <div className="sonoterapia-categories">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              className={`sono-cat-btn ${cat.key === category ? 'active' : ''}`}
              onClick={() => setCategory(cat.key)}
            >
              <span className="sono-icon" aria-hidden>{cat.icon}</span>{cat.label}
            </button>
          ))}
        </div>
        <div className="sonoterapia-desc">{catMeta.desc}</div>
        <div className="sonoterapia-tracks">
          {tracks.map((t, i) => (
            <button
              key={t.file}
              className={`sono-track-btn ${i === currentTrackIdx ? 'active' : ''}`}
              onClick={() => handleChangeTrack(i)}
            >Pista {t.label}</button>
          ))}
        </div>
        <div className="sonoterapia-player-box">
          <audio ref={audioRef} preload="auto" />
          <div className="sonoterapia-progress-bar">
            <div className="sonoterapia-progress" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="sonoterapia-player-actions">
            <button className="sono-play-btn" onClick={handlePlay}>▶ Reproducir</button>
            <button className="sono-stop-btn" onClick={handleStop}>■ Stop</button>
          </div>
          {errorMsg && (
            <div style={{marginTop: 8, color: '#ffb4c8', fontWeight: 600, textShadow: '0 1px 2px #0008'}}>{errorMsg}</div>
          )}
        </div>
        {treatmentDone && (
          <div className="treatment-finished">
            <div className="treatment-finished-inner">
              <h2>Tratamiento completado 🎉</h2>
              <p>Has completado la lista de reproducción. Puedes repetirlo o volver al inicio.</p>
              <div style={{display:'flex',gap:12,marginTop:12}}>
                <button className="sono-play-btn" onClick={() => { setTreatmentDone(false); setCurrentTrackIdx(0); handlePlay(); }}>Repetir</button>
                <button className="sono-stop-btn" onClick={() => { setTreatmentDone(false); onBack(); }}>Volver al inicio</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
